import { PaymentMethodEnum } from '@/types/common';
import type {
  Appearance,
  ShippingRate,
  StripeElementsOptionsMode,
  StripeExpressCheckoutElementOptions,
} from '@stripe/stripe-js';

export const STRIPE_CONFIG = { developerTools: { assistant: { enabled: false } } };

// ACDEV-25067: Stripe requires a `shippingRates` entry whenever
// `shippingAddressRequired` is true — without one the Apple/Google Pay sheet
// hangs on "loading" after an address change. These are digital goods with no
// real shipping, so we expose a single $0 rate purely to satisfy that contract.
export const EXPRESS_FREE_SHIPPING_RATES: ShippingRate[] = [
  {
    id: 'free',
    displayName: 'Free',
    amount: 0,
  },
];

export const STRIPE_APPEARANCE: Appearance = {
  theme: 'stripe',
  /**
   * Skips Stripe's intro fade on every Element render. Pure perf —
   * animations were ~50-200ms per Element on first mount and we never
   * actually wanted the fade. Aligns with v2's `use-stripe-config.ts`
   * which has shipped this in production for ages.
   */
  disableAnimations: true,
  variables: {
    colorPrimary: '#0066cc',
    colorBackground: '#ffffff',
    colorText: '#30313d',
    colorDanger: '#df1b41',
    fontFamily: 'sans-serif',
    spacingUnit: '4px',
    borderRadius: '6px',
    // 16px is the iOS Safari focus-zoom threshold — anything smaller makes the
    // page zoom when a card field is tapped on mobile. Keep all PSP card fields
    // at >=16px (matches Adyen/Nuvei) so no provider triggers the zoom.
    fontSizeBase: '16px',
  },
  rules: {
    '.Input': {
      border: '1px solid #e0e0e0',
      padding: '12px 8px',
      fontSize: '16px',
    },
    '.Input:focus': {
      border: '1px solid #e0e0e0',
      boxShadow: 'none',
    },
    '.Label': {
      fontSize: '0px',
      lineHeight: '0',
    },
    '.Error': {
      fontSize: '0px',
      lineHeight: '0',
    },
  },
};

/**
 * Stripe APM appearance — used by every `<PaymentElement>` form (see
 * `StripeGenericPayForm`, `StripeIdealForm`), i.e. Klarna, iDEAL, Bizum,
 * MB WAY, Affirm, BLIK, CashApp, UPI, PIX, Swish, and any future Stripe APM.
 *
 * ACDEV-26561: `STRIPE_APPEARANCE` collapses `.Label` to `fontSize: 0` for the
 * compact card look. Card mode is unaffected — `StripeCardForm` uses individual
 * `CardNumberElement` / `CardExpiryElement` / `CardCvcElement` which don't
 * render a Stripe-managed `.Label` at all. But `PaymentElement` DOES render a
 * label above each field, and for multi-field APMs (billing address, bank
 * account, etc.) Stripe doesn't emit placeholder text for every field. With
 * labels hidden AND no placeholder, shoppers see a completely blank input with
 * zero indication of what to enter, which hurts conversion.
 *
 * This variant restores the label at a size/spacing that matches the rest of
 * the checkout typography (`colorText` / `fontFamily` inherit from the shared
 * `variables`), so every APM field ships with visible input guidance —
 * regardless of whether the underlying method provides a placeholder.
 */
export const STRIPE_APM_APPEARANCE: Appearance = {
  ...STRIPE_APPEARANCE,
  rules: {
    ...STRIPE_APPEARANCE.rules,
    // Stripe Appearance rules REPLACE the object (not merge), so specifying
    // `.Label` here fully overrides the base's `{ fontSize: 0, lineHeight: 0 }`
    // — everything not set below falls back to Stripe's `stripe`-theme
    // defaults (`fontWeight: 500`, proportional lineHeight off `fontSizeBase`,
    // Stripe-managed label-to-input spacing). The only override we actually
    // need is un-collapsing the size. 16px keeps the label in step with
    // every other checkout surface: `.Input` above (16px),
    // `STRIPE_APPEARANCE.variables.fontSizeBase` (16px), Adyen
    // `.adyen-checkout__input` (`src/styles/adyen.css`), Braintree
    // `BRAINTREE_STYLES.input['font-size']` (`src/constants/braintree.ts`),
    // and Nuvei `NUVEI_FIELD_STYLES.base.fontSize` (`src/constants/nuvei.ts`).
    // All four pin 16px to dodge iOS Safari's <16px focus-zoom.
    '.Label': {
      fontSize: '16px',
    },
  },
};

/**
 * Base options applied to every `<Elements>` instance that wraps an
 * `<ExpressCheckoutElement>` — i.e. PayButtonByDevice (Apple+Google
 * cold-start row), ApplePayStripeForm, GooglePayStripeForm,
 * LinkPayForm, LinkSavedCardForm.
 *
 * Mirror of v2's `DEFAULT_EXPRESS_STRIPE_OPTIONS` (in
 * `independent-checkout-v2/src/components/Checkout/hooks/use-stripe-config.ts`).
 * Two fields, both perf:
 *
 *   - `paymentMethodTypes: ['card']` is a hint to Stripe Elements
 *     about which client-side PM modules to load. ECE wallet rendering
 *     is driven by the element-level `paymentMethods: { applePay,
 *     googlePay, link }` option, NOT by this list — Stripe documents
 *     them as independent. v2 ships exactly this combo (Apple+Google+
 *     Link via ECE *with* `paymentMethodTypes: ['card']`) so this is
 *     a known-good config, not speculation. Restricting to `['card']`
 *     skips Stripe loading bank/redirect/BNPL PM validators that the
 *     wallet flow never touches, which is a measurable cut to the
 *     7-second ECE iframe boot.
 *
 *   - `appearance.disableAnimations: true` skips Stripe's intro fade
 *     on the ECE iframe — animations don't disable themselves just
 *     because the parent `Elements` doesn't request `loader: 'always'`.
 *
 * NOT shipped here intentionally:
 *   - `locale` — v2 sets it but `getLocale()` requires the boot to be
 *     resolved, and we want this constant to be import-safe at
 *     module-eval time. Per-form mounts can override if needed.
 *   - `appearance.variables` — v2's `fontSizeSm: '12px'` is cosmetic;
 *     leaving the express forms in default Stripe styling.
 */
export const EXPRESS_ELEMENTS_BASE_OPTIONS = {
  paymentMethodTypes: ['card'],
  appearance: { disableAnimations: true },
} satisfies Pick<StripeElementsOptionsMode, 'paymentMethodTypes' | 'appearance'>;

export const LINK_PAYMENT_CONFIG: StripeExpressCheckoutElementOptions = {
  paymentMethods: {
    link: 'auto',
    applePay: 'never',
    googlePay: 'never',
  },
};

// `buttonHeight: 48` matches the express wallet visual height. Keep the real
// Stripe-rendered buttons and brand placeholders size-stable.
//
// ACDEV-25067: `shippingAddressRequired` is what makes Stripe emit
// `shippingaddresschange` — the only ECE event that surfaces a postal code
// *before* authorization (it fires as the sheet opens for a default address) —
// letting us recompute and show the latest total in the sheet via
// `elements.update({ amount })`. It only makes sense for customers whose
// jurisdiction actually requires ZIP-based tax (currently US/CA) with
// `WALLET_ZIP_TAX` on, so it is layered on per-render via
// `getStripeExpressCheckoutShippingConfig(customer, featureFlags)` at the call
// sites — not baked into the static config here.
//
// `emailRequired` intentionally NOT here: pre-branch behavior passes it on the
// click `resolve()`. The wallet-tax variants add it to options themselves —
// once any OTHER collection param (e.g. `shippingAddressRequired`) is on
// options Stripe rejects passing them on click.
export const GOOGLE_PAY_PAYMENT_CONFIG: StripeExpressCheckoutElementOptions = {
  paymentMethods: {
    googlePay: 'always',
    link: 'never',
    applePay: 'never',
  },
  buttonHeight: 48,
};

export const APPLE_PAY_PAYMENT_CONFIG: StripeExpressCheckoutElementOptions = {
  paymentMethods: {
    applePay: 'always',
    link: 'never',
    googlePay: 'never',
  },
  buttonHeight: 48,
};

// Stripe's ECE only accepts `'auto' | 'never'` for Amazon Pay (unlike Apple/
// Google Pay which also allow `'always'` — see
// `ExpressCheckoutPaymentMethodsOption`). So we cannot bypass Stripe's probe:
// if `onReady` fires with `availablePaymentMethods: undefined` or
// `amazonPay: false`, that's binding.
// `buttonHeight: 48` pins the ECE-rendered button to the 48px shell row so it
// visually matches the placeholder — same value as Apple/Google Pay above.
export const AMAZON_PAY_PAYMENT_CONFIG: StripeExpressCheckoutElementOptions = {
  paymentMethods: {
    amazonPay: 'auto',
    link: 'never',
    googlePay: 'never',
    applePay: 'never',
    klarna: 'never',
  },
  buttonHeight: 48,
};

// Stripe payment methods whose `next_action` is rendered in-page via
// `stripe.handleNextAction({ clientSecret })` (e.g. a QR-code modal) rather
// than as a hosted redirect URL. On desktop these are rendered in-place; on
// mobile (where supported), they are handed off via the local
// `LocationRoutes.PAYMENT_HANDLER` route so a popup can run `handleNextAction`
// and deep-link into a wallet app.
export const stripeInPageNextActionPaymentMethods: PaymentMethodEnum[] = [
  PaymentMethodEnum.CASH_APP,
  PaymentMethodEnum.UPI,
];

// Generic Stripe APMs whose `next_action` resolves in-page via
// `stripe.handleNextAction` and carry NO hosted `redirect_to_url`, so they
// NEVER need a pre-opened redirect tab:
//   - Bizum: banking-app approval / same-window modal.
//   - MB WAY: shopper approves in the MB WAY app (phone-based); no redirect URL.
// Neither has a redirect that could navigate the iframe, so running
// `handleNextAction` in-page is safe. Distinct from
// `stripeInPageNextActionPaymentMethods` (CASH_APP / UPI QR, which still hand
// off to a popup on mobile). This is the single source of truth used by
// `isRequireToOpenTab` (no tab) and `getStripePostFinalizeAction` (route to the
// in-page next-action driver).
export const stripeInPageActionApmMethods: PaymentMethodEnum[] = [PaymentMethodEnum.BIZUM, PaymentMethodEnum.MBWAY];

/**
 * Stripe `<PaymentElement>` APMs that should receive saved-customer
 * `defaultValues.billingDetails.address`. All other Stripe APMs routed through
 * `StripeGenericPayForm` get name-only defaults (email is collected separately
 * via `<Email />`).
 */
export const stripeApmPaymentElementAddressMethods: PaymentMethodEnum[] = [PaymentMethodEnum.UPI];

// Stripe APMs that must run `handleNextAction` in the top-level PAYMENT_HANDLER
// tab on ALL devices (never in-page), because their `next_action` exposes both
// a QR and an app deep link together and Stripe.js's OWN device detection (which
// we can't override) decides which to use. Running it inline lets that deep link
// navigate the checkout iframe itself, breaking the embedded flow — on desktop
// and mobile alike. Routing through the top-level tab keeps Stripe's built-in,
// localized QR + "Open app" UI while the tab (not the iframe) owns the redirect.
//   - Swish: `swish_handle_redirect_or_display_qr_code`.
// Unlike `stripeInPageNextActionPaymentMethods` (tab only on mobile, in-page QR
// on desktop), these always open the tab. Used by `isRequireToOpenTab` (tab) and
// `usePaymentResults` (rewrite the redirect URL to the PAYMENT_HANDLER route).
export const stripePaymentHandlerTabMethods: PaymentMethodEnum[] = [PaymentMethodEnum.SWISH];

// Stripe methods that render an in-page QR/voucher on the post-finalize step.
// PIX always renders in-page; CASH_APP / UPI render a QR on desktop (and hand
// off to a popup on mobile — see `stripeInPageNextActionPaymentMethods`, which
// stays the source of truth for that platform-specific behavior). This combined
// list exists only so `getStripePostFinalizeAction` can classify the QR bucket
// without special-casing PIX.
export const stripeInPageQrMethods: PaymentMethodEnum[] = [
  PaymentMethodEnum.PIX,
  ...stripeInPageNextActionPaymentMethods,
];

export const GOOGLE_PAY_WALLET = 'google_pay';
export const APPLE_PAY_WALLET = 'apple_pay';
export const LINK_PAYMENT_WALLET = 'link';
